import React, { useRef, useState } from 'react';

import classNames from 'classnames';

import classes from './styles.module.scss';
import AdminService from '../../../../../../../services/AdminService';

const ACCEPT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

export default function SingleFileUploader({
  name,
  label,
  placeholderTitle,
  placeholderHint,
  value,
  setFieldValue,
  setFieldTouched,
  readonly,
  height = 110,
  uploadImmediately = false,
  uploadingText,
  uploadFailedText,
}) {
  const inputRef = useRef();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const toMeta = (uploaded, fallback) => ({
    fileName: uploaded?.fileName || fallback?.name,
    url: uploaded?.uri || uploaded?.url,
    mimeType: uploaded?.mimeType || fallback?.type,
    displayName: uploaded?.displayName || fallback?.name,
  });

  const handleUploadAndSet = async (file) => {
    if (!file) return;
    if (!uploadImmediately) {
      setFieldValue?.(name, file);
      setFieldTouched?.(name, true, true);
      return;
    }
    try {
      setIsUploading(true);
      setUploadError('');
      const uploaded = await AdminService.uploadFile(file);
      const meta = toMeta(uploaded, file);
      setFieldValue?.(name, meta);
      setFieldTouched?.(name, true, true);
    } catch (e) {
      setUploadError(uploadFailedText || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const onSelect = (e) => {
    const [file] = Array.from(e.target.files || []);
    if (!file) return;
    handleUploadAndSet(file);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (readonly) return;
    const [file] = Array.from(e.dataTransfer.files || []);
    if (!file) return;
    handleUploadAndSet(file);
  };

  return (
    <div className={classes.Uploader}>
      {label && <span className={classes.label}>{label}</span>}
      <div
        className={classNames(classes.box, { [classes.readonly]: readonly })}
        style={{ height }}
        onClick={() => {
          if (readonly) return;
          inputRef.current?.click();
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        {!readonly && (
          <button
            type="button"
            className={classes.addBtn}
            aria-label="Add"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            +
          </button>
        )}
        {value ? (
          <div className={classes.fileRow}>
            <span className={classes.fileName}>
              {value?.name || value?.displayName}
            </span>
            {!readonly && (
              <button
                type="button"
                className={classes.deleteBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setFieldValue?.(name, null);
                  setFieldTouched?.(name, true, true);
                }}
                aria-label="Remove file"
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <div className={classes.placeholder}>
            <div className={classes.title}>{placeholderTitle}</div>
            <div className={classes.hint}>
              {isUploading ? uploadingText || 'Uploading...' : placeholderHint}
            </div>
            {uploadError && (
              <div className={classes.hint} style={{ color: '#d33' }}>
                {uploadError}
              </div>
            )}
          </div>
        )}
      </div>
      <input
        type="file"
        ref={inputRef}
        accept={ACCEPT}
        onChange={onSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}
